import { TranslateFunction } from 'b2b-server/core/utils/language';

export enum B2BRegistrationStep {
  UIDEntry = 0,
  CompanyDetails = 1,
  ContactDetails = 2,
  BusinessInfo = 3,
  PartnerCloud = 4,
  FocusFields = 5,
  Review = 6,
  Confirmation = 7,
}

export const FORM_STORAGE_KEY = '@alltron-b2b-registration';
export const PROD_FORM_ID = '6988c838441404f0e84f7bc4';
export const STAGE_FORM_ID = '6988c765f55afce4066f2b97';

export const SWISS_UID_REGEX = {
  FORMATTED: /^CHE-\d{3}\.\d{3}\.\d{3}$/,
  NO_SEPARATORS: /^CHE\d{9}$/,
  NUMERIC_WITH_DOTS: /^\d{3}\.\d{3}\.\d{3}$/,
  NUMERIC_ONLY: /^\d{9}$/,
} as const;

export const SWISS_UID_REGISTER_API_ENDPOINT = 'https://www.uid-wse.admin.ch/V5.0/PublicServices.svc';
export const SWISS_UID_REGISTER_API_TIMEOUT = 10000;

export const INPUT_NAMES = {
  uid: 'uid',
  uidVerificationFailed: 'uidVerificationFailed',
  uidPrefilledDataUsed: 'uidPrefilledDataUsed',
  legalForm: 'legalForm',
  companyName: 'companyName',
  companyLegalName: 'companyLegalName',
  companyNameAddition: 'companyNameAddition',
  street: 'street',
  streetNumber: 'streetNumber',
  addressAddition: 'addressAddition',
  zipCode: 'zipCode',
  city: 'city',
  country: 'country',
  companyEmail: 'companyEmail',
  websiteUrl: 'websiteUrl',
  webshopAccountEmail: 'webshopAccountEmail',
  contactSalutation: 'contactSalutation',
  contactFirstName: 'contactFirstName',
  contactLastName: 'contactLastName',
  contactRole: 'contactRole',
  contactPhone: 'contactPhone',
  contactEmail: 'contactEmail',
  applicantSameAsContact: 'applicantSameAsContact',
  applicantSalutation: 'applicantSalutation',
  applicantFirstName: 'applicantFirstName',
  applicantLastName: 'applicantLastName',
  applicantRole: 'applicantRole',
  applicantPhone: 'applicantPhone',
  applicantEmail: 'applicantEmail',
  businessTypes: 'businessTypes',
  employeeCount: 'employeeCount',
  officeWorkplaces: 'officeWorkplaces',
  plannedRevenue: 'plannedRevenue',
  nogaNumber: 'nogaNumber',
  partnerProgrammes: 'partnerProgrammes',
  certifications: 'certifications',
  cloudServiceInterest: 'cloudServiceInterest',
  cloudServicePhone: 'cloudServicePhone',
  focusFields: 'focusFields',
  termsAccepted: 'termsAccepted',
  newsletterSubscription: 'newsletterSubscription',
  newsletterIT: 'newsletterIT',
  newsletterElektro: 'newsletterElektro',
  newsletterTelco: 'newsletterTelco',
  newsletterProAV: 'newsletterProAV',
  newsletterSecurity: 'newsletterSecurity',
} as const;

export type InputNameKey = keyof typeof INPUT_NAMES;

export const SALUTATION_OPTIONS = ['male', 'female', 'none'] as const;
export type SalutationType = typeof SALUTATION_OPTIONS[number];

export const getSalutationLabels = (t: TranslateFunction): Record<SalutationType, string> => ({
  male: t('Mr'),
  female: t('Ms'),
  none: t('None'),
});

export const APPLICANT_SELECTION_OPTIONS = ['contactPersonAbove', 'newContact'] as const;
export type ApplicantSelectionType = typeof APPLICANT_SELECTION_OPTIONS[number];

export const getApplicantSelectionLabels = (t: TranslateFunction): Record<ApplicantSelectionType, string> => ({
  contactPersonAbove: t('Contact person above'),
  newContact: t('New contact'),
});

export const CONTACT_ROLE_OPTIONS = [
  'managingDirector',
  'headOfPurchasing',
  'headOfSales',
  'headOfEngineering',
  'headOfService',
  'headOfMarketing',
  'projectManager',
  'buyer',
  'salesperson',
  'administration',
  'other',
] as const;
export type ContactRoleType = typeof CONTACT_ROLE_OPTIONS[number];

export const getContactRoleLabels = (t: TranslateFunction): Record<ContactRoleType, string> => ({
  managingDirector: t('Managing Director'),
  headOfPurchasing: t('Head of Purchasing'),
  headOfSales: t('Head of Sales'),
  headOfEngineering: t('Head of Engineering'),
  headOfService: t('Head of Service'),
  headOfMarketing: t('Head of Marketing'),
  projectManager: t('Project Manager'),
  buyer: t('Buyer'),
  salesperson: t('Salesperson'),
  administration: t('Administration'),
  other: t('Other'),
});

export const BUSINESS_TYPE_OPTIONS = [
  'physicalStore',
  'onlineShop',
  'solutionProvider',
  'carrierServiceProvider',
  'other',
] as const;
export type BusinessType = typeof BUSINESS_TYPE_OPTIONS[number];

export const getBusinessTypeLabels = (t: TranslateFunction): Record<BusinessType, string> => ({
  physicalStore: t('Physical Store'),
  onlineShop: t('Online Shop'),
  solutionProvider: t('Solution Provider'),
  carrierServiceProvider: t('Carrier/Service Provider'),
  other: t('Other'),
});

export const PLANNED_REVENUE_OPTIONS = [
  'lessThan10k',
  '10kTo25k',
  '25kTo50k',
  '50kTo100k',
  '100kTo250k',
  'moreThan250k',
] as const;
export type PlannedRevenueType = typeof PLANNED_REVENUE_OPTIONS[number];

export const getPlannedRevenueLabels = (t: TranslateFunction): Record<PlannedRevenueType, string> => ({
  lessThan10k: t('Less than CHF 10,000'),
  '10kTo25k': t('CHF 10,000 to 25,000'),
  '25kTo50k': t('CHF 25,000 to 50,000'),
  '50kTo100k': t('CHF 50,000 to 100,000'),
  '100kTo250k': t('CHF 100,000 to 250,000'),
  moreThan250k: t('More than CHF 250,000'),
});

export const FOCUS_FIELD_OPTIONS = [
  'computingCloud',
  'networkSecurity',
  'telcoUcc',
  'proAvMultimedia',
  'electroHousehold',
  'officeLeisure',
] as const;

export const FOCUS_FIELD_HIERARCHY: { id: FocusFieldType; children: FocusFieldType[] }[] = [
  {
    id: 'computingCloud',
    children: [
      'pcNotebooks',
      'peripherals',
      'software',
      'cloudServices',
    ],
  },
  {
    id: 'networkSecurity',
    children: [
      'network',
      'serverDatacenter',
      'itSecurity',
      'videoSurveillance',
    ],
  },
  {
    id: 'telcoUcc',
    children: [
      'telephoneSystems',
      'conferencingWorkSmart',
      'mobileTelephone',
    ],
  },
  {
    id: 'proAvMultimedia',
    children: [
      'proAvProjection',
      'tvAudio',
      'photoVideo',
    ],
  },
  {
    id: 'electroHousehold',
    children: [
      'buildingTechnology',
      'electroLighting',
      'electromobility',
      'solarManagement',
      'householdDevices',
    ],
  },
  {
    id: 'officeLeisure',
    children: [
      'officeSupplies',
      'toolsDoItGarden',
      'leisure',
      'gaming',
      'rcModelBuilding',
      'supermarketDrugstore',
      'various',
    ],
  },
] as const;


export type FocusFieldType =
| 'computingCloud'
| 'networkSecurity'
| 'telcoUcc'
| 'proAvMultimedia'
| 'electroHousehold'
| 'officeLeisure'
| 'pcNotebooks'
| 'peripherals'
| 'software'
| 'cloudServices'
| 'network'
| 'serverDatacenter'
| 'itSecurity'
| 'videoSurveillance'
| 'telephoneSystems'
| 'conferencingWorkSmart'
| 'mobileTelephone'
| 'proAvProjection'
| 'tvAudio'
| 'photoVideo'
| 'buildingTechnology'
| 'electroLighting'
| 'electromobility'
| 'solarManagement'
| 'householdDevices'
| 'officeSupplies'
| 'toolsDoItGarden'
| 'leisure'
| 'gaming'
| 'rcModelBuilding'
| 'supermarketDrugstore'
| 'various';

export const getFocusFieldLabels = (t: TranslateFunction): Record<FocusFieldType, string> => ({
  computingCloud: t('Computing & Cloud'),
  networkSecurity: t('Network & Security'),
  telcoUcc: t('Telco & UCC'),
  proAvMultimedia: t('Pro AV & Multimedia'),
  electroHousehold: t('Electro & Household'),
  officeLeisure: t('Office & Leisure'),
  pcNotebooks: t('PC/Notebooks'),
  peripherals: t('Peripherals/Components'),
  software: t('Software'),
  cloudServices: t('Cloud/Services'),
  network: t('Network'),
  serverDatacenter: t('Server/Datacenter'),
  itSecurity: t('IT Security'),
  videoSurveillance: t('Video Surveillance/Physical Security'),
  telephoneSystems: t('Telephone Systems'),
  conferencingWorkSmart: t('Conferencing/Work Smart'),
  mobileTelephone: t('Mobile Phones'),
  proAvProjection: t('Pro AV/Projection'),
  tvAudio: t('TV/Audio'),
  photoVideo: t('Photo/Video'),
  buildingTechnology: t('Building Technology'),
  electroLighting: t('Electro/Lighting'),
  electromobility: t('Electromobility'),
  solarManagement: t('Solar Management'),
  householdDevices: t('Household Devices'),
  officeSupplies: t('Office Supplies'),
  toolsDoItGarden: t('Tools/Do-It & Garden'),
  leisure: t('Leisure'),
  gaming: t('Gaming'),
  rcModelBuilding: t('RC Model Building'),
  supermarketDrugstore: t('Supermarket/Drugstore'),
  various: t('Various'),
});

export const NEWSLETTER_OPTIONS = [
  'newsletterIT',
  'newsletterElektro',
  'newsletterTelco',
  'newsletterProAV',
  'newsletterSecurity',
] as const;
export type NewsletterType = typeof NEWSLETTER_OPTIONS[number];

export const getNewsletterLabels = (t: TranslateFunction): Record<NewsletterType, string> => ({
  newsletterIT: t('IT Newsletter'),
  newsletterElektro: t('Elektro Newsletter'),
  newsletterTelco: t('Telco Newsletter'),
  newsletterProAV: t('Pro AV Newsletter'),
  newsletterSecurity: t('Security Newsletter'),
});

export const MAX_LENGTH = {
  companyName: 50,
  companyNameAddition: 50,
  street: 50,
  streetNumber: 10,
  addressAddition: 50,
  city: 50,
  companyEmail: 100,
  websiteUrl: 200,
  webshopAccountEmail: 100,
  firstName: 50,
  lastName: 50,
  phone: 30,
  email: 100,
  employeeCount: 10,
  officeWorkplaces: 10,
  nogaNumber: 10,
  partnerProgrammes: 500,
  certifications: 500,
} as const;
